import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, query, where, writeBatch } from 'firebase/firestore';
import { ArrowLeft, Edit2, Trash2, Plus, Save, X, Merge, Loader2, CheckCircle, Download, FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Firm } from '../types';

interface MergeOperation {
  from: string;
  to: string;
  count: number;
}

export default function ManageCompanies() {
  const { signOut } = useAuth();
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedFirms, setSelectedFirms] = useState<Set<string>>(new Set());
  const [canonicalName, setCanonicalName] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState<{ current: number; total: number } | null>(null);
  const [mergeResults, setMergeResults] = useState<MergeOperation[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchFirms();
  }, []);

  const fetchFirms = async () => {
    try {
      const firmsSnapshot = await getDocs(collection(db, 'firms'));
      const firmsData = firmsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as (Firm & { id: string })[];
      
      // Sort firms alphabetically by name
      firmsData.sort((a, b) => a.name.localeCompare(b.name));
      setFirms(firmsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching firms:', error);
      setLoading(false);
    }
  };

  const handleEdit = (firm: Firm & { id: string }) => {
    setEditingId(firm.id);
    setEditingName(firm.name);
  };

  const handleSave = async (id: string) => {
    if (!editingName.trim()) return;
    
    try {
      await updateDoc(doc(db, 'firms', id), {
        name: editingName.trim()
      });
      
      setFirms(firms.map(firm => 
        firm.id === id ? { ...firm, name: editingName.trim() } : firm
      ));
      
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      console.error('Error updating firm:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette dette busselskapet?')) return;
    
    try {
      await deleteDoc(doc(db, 'firms', id));
      setFirms(firms.filter(firm => firm.id !== id));
    } catch (error) {
      console.error('Error deleting firm:', error);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    
    try {
      const docRef = await addDoc(collection(db, 'firms'), {
        name: newCompanyName.trim()
      });
      
      const newFirm = {
        id: docRef.id,
        name: newCompanyName.trim()
      };
      
      setFirms([...firms, newFirm].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCompanyName('');
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding firm:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleFirmSelection = (firmName: string, checked: boolean) => {
    const newSelected = new Set(selectedFirms);
    if (checked) {
      newSelected.add(firmName);
    } else {
      newSelected.delete(firmName);
    }
    setSelectedFirms(newSelected);
    
    // Reset canonical name if it's no longer in selection
    if (!newSelected.has(canonicalName)) {
      setCanonicalName(newSelected.size > 0 ? Array.from(newSelected)[0] : '');
    }
  };

  const executeMerge = async () => {
    if (selectedFirms.size < 2) {
      alert('Velg minst 2 firmaer for å slå sammen');
      return;
    }

    if (!canonicalName) {
      alert('Velg et kanonisk navn');
      return;
    }

    const firmsToMerge = Array.from(selectedFirms).filter(name => name !== canonicalName);
    
    if (!confirm(`Er du sikker på at du vil slå sammen ${firmsToMerge.length} firmanavn til "${canonicalName}"? Dette kan ikke angres.`)) {
      return;
    }

    setMerging(true);
    setMergeProgress({ current: 0, total: firmsToMerge.length });
    const results: MergeOperation[] = [];

    try {
      let processed = 0;
      
      // Process merges in batches
      const batchSize = 10;
      
      for (let i = 0; i < firmsToMerge.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchFirms = firmsToMerge.slice(i, i + batchSize);
        
        for (const fromName of batchFirms) {
          // Find all registrations with the old firm name
          const registrationsSnapshot = await getDocs(
            query(collection(db, 'registrations'), where('busCompany', '==', fromName))
          );
          
          const count = registrationsSnapshot.docs.length;
          if (count > 0) {
            // Update all registrations
            registrationsSnapshot.docs.forEach(docSnapshot => {
              batch.update(docSnapshot.ref, { busCompany: canonicalName });
            });
            
            results.push({ from: fromName, to: canonicalName, count });
          }
          
          processed++;
          setMergeProgress({ current: processed, total: firmsToMerge.length });
        }
        
        // Commit the batch
        await batch.commit();
      }

      // Clean up duplicate firms in the firms collection
      const firmsToDelete = firms.filter(firm => 
        firmsToMerge.includes(firm.name)
      );
      
      // Delete old firm entries
      for (const firm of firmsToDelete) {
        if (firm.id) {
          await deleteDoc(doc(db, 'firms', firm.id));
        }
      }

      setMergeResults(results);
      setShowResults(true);
      
      // Refresh the firms list
      await fetchFirms();
      
      // Reset selection state
      setSelectedFirms(new Set());
      setCanonicalName('');
      
    } catch (error) {
      console.error('Error executing merge:', error);
      alert('Feil ved sammenslåing av data');
    } finally {
      setMerging(false);
      setMergeProgress(null);
    }
  };

  const downloadReport = () => {
    const csvContent = [
      'Fra,Til,Antall registreringer',
      ...mergeResults.map(result => `${result.from},${result.to},${result.count}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `firma_sammenslåing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const resetResults = () => {
    setShowResults(false);
    setMergeResults([]);
  };

  const exportCompanies = () => {
    const exportData = firms.map(firm => ({
      name: firm.name
    }));

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `busselskaper_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link
                to="/"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til dashboard
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Administrer busselskaper</h1>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {showResults && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Sammenslåing fullført</h2>
                    <p className="text-gray-600">
                      {mergeResults.length} firmanavn ble slått sammen, 
                      totalt {mergeResults.reduce((sum, r) => sum + r.count, 0)} registreringer oppdatert
                    </p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={downloadReport}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Last ned rapport
                  </button>
                  <button
                    onClick={resetResults}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Lukk
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Til
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registreringer oppdatert
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mergeResults.map((result, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {result.from}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {result.to}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {result.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">
                Busselskaper ({firms.length})
              </h2>
              <div className="flex items-center space-x-3">
                {selectedFirms.size > 1 && (
                  <div className="flex items-center space-x-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-900">
                      {selectedFirms.size} firmaer valgt
                    </span>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-blue-900">
                        Slå sammen til:
                      </label>
                      <select
                        value={canonicalName}
                        onChange={(e) => setCanonicalName(e.target.value)}
                        className="rounded-md border-blue-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Velg kanonisk navn</option>
                        {Array.from(selectedFirms).map(firmName => (
                          <option key={firmName} value={firmName}>
                            {firmName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={executeMerge}
                      disabled={merging || !canonicalName || selectedFirms.size < 2}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
                    >
                      {merging ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Slår sammen...
                        </>
                      ) : (
                        <>
                          <Merge className="h-4 w-4 mr-2" />
                          Slå sammen
                        </>
                      )}
                    </button>
                  </div>
                )}
                <button
                  onClick={exportCompanies}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  Eksporter JSON
                </button>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Legg til nytt selskap
                </button>
              </div>
            </div>

            {showAddForm && (
              <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      placeholder="Navn på nytt busselskap"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCompany()}
                    />
                  </div>
                  <button
                    onClick={handleAddCompany}
                    disabled={!newCompanyName.trim()}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Lagre
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setNewCompanyName('');
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Avbryt
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Velg
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Selskapsnavn
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {firms.map((firm, index) => (
                    <tr key={firm.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedFirms.has(firm.name)}
                          onChange={(e) => handleFirmSelection(firm.name, e.target.checked)}
                          disabled={editingId === firm.id}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingId === firm.id ? (
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            onKeyPress={(e) => e.key === 'Enter' && handleSave(firm.id)}
                            autoFocus
                          />
                        ) : (
                          <div className="text-sm font-medium text-gray-900">{firm.name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingId === firm.id ? (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleSave(firm.id)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Lagre
                            </button>
                            <button
                              onClick={handleCancel}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Avbryt
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(firm as Firm & { id: string })}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-600 hover:text-blue-900"
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Rediger
                            </button>
                            <button
                              onClick={() => handleDelete(firm.id)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Slett
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {firms.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">Ingen busselskaper registrert</p>
                    <p className="mt-2">Klikk på "Legg til nytt selskap" for å komme i gang.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Progress bar for merge operation */}
            {mergeProgress && (
              <div className="px-6 py-4 border-t border-gray-200 bg-blue-50">
                <div className="flex justify-between text-sm text-blue-700 mb-1">
                  <span>Behandler sammenslåinger...</span>
                  <span>{mergeProgress.current} / {mergeProgress.total}</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(mergeProgress.current / mergeProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}